<script>
  import Uppy from '@uppy/core'
  import AwsS3Multipart from '@uppy/aws-s3-multipart'
  import { onMount, onDestroy, tick, untrack } from 'svelte'
  import { useForm, page } from '@inertiajs/svelte'
  import Drawer from './drawer.svelte'
  import SelectField from './form/select_field.svelte'
  import { Button } from './ui'

  // File type is the one check the browser makes: the server can't see a file before upload.
  const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png'

  // Uppy puts each file straight in the Shrine cache (S3 multipart via the same-origin
  // "/s3/multipart" companion mount, AdminSite::S3MultipartAuth), then ONE Inertia POST
  // /media creates the batch's Image records. media#create redirects back to the open page
  // and hands the created images back as flash data, so each reaches `onuploaded(image)`.
  // `only` limits the post's redirect to the props the caller needs (default: just flash,
  // so a form page keeps its props); the Media page asks for its images too.
  // Per-file fields are checked live against media#create (Precognition, `images.<n>.<field>`);
  // a blank title is filled in there from the file name.
  // Fresh Uppy instance per mounted component (the old CMS's module-level singleton was a
  // known bug).
  let { open = $bindable(false), placeId = null, endpoint = '/media', only = ['flash'], onuploaded } = $props()

  // `files` is display state (name + preview), index-aligned with `$form.images`.
  let files = $state([])
  let uploading = $state(false)
  let error = $state(null)
  let placeChoice = $state('')
  let uppy

  const form = useForm({ images: [] }).withPrecognition('post', () => endpoint)

  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

  $effect(() => {
    if (!open) return
    untrack(() => {
      placeChoice = placeId || ''
      error = null
      $form.clearErrors()
    })
  })

  const syncFiles = () => {
    const next = uppy.getFiles()
    $form.images = next.map((file) => {
      const index = files.findIndex((entry) => entry.id === file.id)
      return index >= 0 ? $form.images[index] : { title: '', all_tags: '', place_id: placeChoice }
    })
    files = next.map((file) => files.find((entry) => entry.id === file.id) ?? { id: file.id, name: file.name, preview: URL.createObjectURL(file.data) })
  }

  const onPlaceChange = async () => {
    await tick()
    $form.images = $form.images.map((entry) => ({ ...entry, place_id: placeChoice }))
    if ($form.images.length > 0) $form.validate({ only: $form.images.map((_, index) => `images.${index}.place_id`) })
  }

  const reset = () => {
    for (const file of files) URL.revokeObjectURL(file.preview)
    files = []
    $form.images = []
    uppy.cancelAll()
  }

  // Only the uploaded files are posted. A partly failed batch keeps the drawer open on the
  // failed files, so the admin can retry them.
  const createImages = (uploaded) => {
    const failedIds = files.filter((file) => !uploaded.has(file.id)).map((file) => file.id)
    const keep = files.map((file, index) => [file, index]).filter(([file]) => uploaded.has(file.id))
    const pending = $form.images
    $form.images = keep.map(([file, index]) => ({ ...pending[index], image: uploaded.get(file.id) }))

    $form.post(endpoint, {
      preserveState: true,
      preserveScroll: true,
      only,
      onFlash: (flash) => (flash.created_images ?? []).forEach((image) => onuploaded?.(image)),
      onSuccess: () => {
        if (failedIds.length === 0) {
          reset()
          open = false
          return
        }
        for (const [file] of keep) removeFile(file)
      },
      onError: () => {
        $form.images = pending
      },
      onFinish: () => (uploading = false)
    })
  }

  onMount(() => {
    uppy = new Uppy({ autoProceed: false, restrictions: { allowedFileTypes: ACCEPTED_IMAGE_TYPES.split(',') } }).use(AwsS3Multipart, {
      companionUrl: '/',
      companionHeaders: { 'X-CSRF-Token': csrfToken() }
    })

    uppy.on('files-added', syncFiles)
    uppy.on('file-removed', syncFiles)

    uppy.on('complete', (result) => {
      const failed = result.failed?.length ?? 0
      if (failed > 0) error = `${failed} ${failed === 1 ? 'file' : 'files'} failed to upload. Remove ${failed === 1 ? 'it' : 'them'} or try again.`

      // Shrine's cache JSON per file, keyed by the companion's …/cache/<key> upload URL.
      const uploaded = new Map()
      for (const file of result.successful ?? []) {
        const match = file.response?.uploadURL?.match(/\/cache\/([^?]+)/)
        if (!match) continue
        uploaded.set(
          file.id,
          JSON.stringify({ id: decodeURIComponent(match[1]), storage: 'cache', metadata: { size: file.size, filename: file.name, mime_type: file.type } })
        )
      }
      if (uploaded.size === 0) {
        uploading = false
        return
      }
      createImages(uploaded)
    })
  })

  onDestroy(() => {
    for (const file of files) URL.revokeObjectURL(file.preview)
    uppy?.close()
  })

  const addFiles = (list) => {
    error = null
    for (const file of list) {
      try {
        uppy.addFile({ name: file.name, type: file.type, data: file })
      } catch {
        error = `${file.name} isn't a JPEG or PNG image.`
      }
    }
  }

  const onFilePick = (event) => {
    addFiles(event.target.files)
    event.target.value = ''
  }

  const onDrop = (event) => {
    event.preventDefault()
    addFiles(event.dataTransfer.files)
  }

  const removeFile = (file) => {
    URL.revokeObjectURL(file.preview)
    uppy.removeFile(file.id)
  }

  const startUpload = () => {
    error = null
    $form.clearErrors()
    uploading = true
    uppy.upload()
  }
</script>

{#snippet uploadFooter()}
  <Button variant="primary" size="sm" block loading={uploading} onclick={startUpload}>
    {uploading ? 'Uploading…' : `Upload ${files.length} ${files.length === 1 ? 'image' : 'images'}`}
  </Button>
{/snippet}

<Drawer bind:open title="Upload images" footer={files.length > 0 ? uploadFooter : undefined}>
  <div class="space-y-4">
    <SelectField
      bind:value={placeChoice}
      label="Place"
      name="place_id"
      source="places"
      sourceParams={{ area_id: $page.props.current_area_id, with_area: 1 }}
      placeholder="No place"
      onchange={onPlaceChange}
      error={$form.errors['images.0.place_id']}
      help="Tags every image in this upload with the place."
    />

    <div
      class="border-2 border-dashed border-base-300 rounded-box p-6 text-center"
      role="region"
      aria-label="Drop images here"
      ondragover={(event) => event.preventDefault()}
      ondrop={onDrop}
    >
      <p class="text-sm text-base-content/60 mb-2">Drag JPEG or PNG files here or</p>
      <input type="file" multiple accept={ACCEPTED_IMAGE_TYPES} onchange={onFilePick} class="file-input file-input-sm" disabled={uploading} />
    </div>

    {#each [error, $form.errors.base].filter(Boolean) as message (message)}
      <p class="text-sm text-error">{message}</p>
    {/each}

    {#if files.length > 0}
      <div class="flex flex-col gap-3">
        {#each files as file, index (file.id)}
          <div class="flex gap-3 border border-base-300 rounded-box p-2">
            <img src={file.preview} alt={file.name} class="h-16 w-16 shrink-0 object-cover rounded-box bg-base-200" />
            <div class="flex flex-col gap-1 grow min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium truncate grow" title={file.name}>{file.name}</span>
                <Button variant="ghost" size="xs" square icon="close" aria-label={`Remove ${file.name}`} disabled={uploading} onclick={() => removeFile(file)} />
              </div>
              {#if $form.images[index]}
                <input
                  type="text"
                  placeholder="Title (defaults to the file name)"
                  aria-label="Title"
                  bind:value={$form.images[index].title}
                  onchange={() => $form.validate(`images.${index}.title`)}
                  class="input input-xs w-full"
                />
                {#if $form.errors[`images.${index}.title`]}<p class="text-xs text-error">{$form.errors[`images.${index}.title`]}</p>{/if}
                <input
                  type="text"
                  placeholder="Tags (comma separated)"
                  aria-label="Tags"
                  bind:value={$form.images[index].all_tags}
                  onchange={() => $form.validate(`images.${index}.all_tags`)}
                  class="input input-xs w-full"
                />
                {#if $form.errors[`images.${index}.all_tags`]}<p class="text-xs text-error">{$form.errors[`images.${index}.all_tags`]}</p>{/if}
                {#if $form.errors[`images.${index}.image`]}<p class="text-xs text-error">{$form.errors[`images.${index}.image`]}</p>{/if}
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</Drawer>
